/**
 * Messages List Page (Admin)
 *
 * Displays a list of all sent messages with:
 * - Filtering by message type
 * - Pagination
 * - Compose message button
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listMessages,
  type Message,
  type MessageType,
  type ListMessagesParams,
} from '../../../api/messages';
import { getErrorMessage } from '../../../api/client';
import { ComposeMessageModal, PendingAssignmentNotifications } from '../../../components/messages';

type FilterType = MessageType | 'ALL';

const MESSAGE_TYPE_CONFIG: Record<MessageType, { label: string; className: string }> = {
  BROADCAST: { label: 'Direct Message', className: 'bg-blue-100 text-blue-800' },
  ASSIGNMENT: { label: 'Assignment', className: 'bg-purple-100 text-purple-800' },
  CREW_REPLY: { label: 'Crew Reply', className: 'bg-green-100 text-green-800' },
  EVENT_PUBLISHED: { label: 'New Event', className: 'bg-teal-100 text-teal-800' },
  EVENT_UPDATED: { label: 'Event Update', className: 'bg-amber-100 text-amber-800' },
};

export default function MessagesPage() {
  const navigate = useNavigate();

  // State
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showComposeModal, setShowComposeModal] = useState(false);

  // Filters
  const [typeFilter, setTypeFilter] = useState<FilterType>('ALL');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: ListMessagesParams = {
        page,
        limit,
      };

      if (typeFilter !== 'ALL') {
        params.messageType = typeFilter;
      }

      const response = await listMessages(params);
      setMessages(response.messages);
      setTotalPages(response.pagination.totalPages);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Handle type filter change
  const handleTypeChange = (type: FilterType) => {
    setTypeFilter(type);
    setPage(1);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div>
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
          <p className="text-gray-600 mt-1">
            Send and manage messages to your crew
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => setShowComposeModal(true)}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Compose Message
          </button>
        </div>
      </div>

      {/* Pending notifications by crew member */}
      <PendingAssignmentNotifications />

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Type filter */}
          <div className="sm:w-48">
            <label htmlFor="type" className="sr-only">Filter by type</label>
            <select
              id="type"
              value={typeFilter}
              onChange={(e) => handleTypeChange(e.target.value as FilterType)}
              className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rack focus:border-rack"
            >
              <option value="ALL">All Types</option>
              <option value="BROADCAST">Direct Message</option>
              <option value="EVENT_PUBLISHED">New Event</option>
              <option value="EVENT_UPDATED">Event Update</option>
              <option value="ASSIGNMENT">Assignment</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Messages table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rack border-r-transparent" />
            <p className="mt-2 text-gray-600">Loading messages...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="p-8 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No messages yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              {typeFilter !== 'ALL'
                ? 'Try adjusting your filters'
                : 'Get started by composing a message'}
            </p>
            {typeFilter === 'ALL' && (
              <button
                onClick={() => setShowComposeModal(true)}
                className="mt-4 inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Compose Message
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recipients
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Channels
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {messages.map((message) => {
                    const typeConfig = MESSAGE_TYPE_CONFIG[message.messageType];
                    return (
                      <tr
                        key={message.id}
                        onClick={() => navigate(`/admin/messages/${message.id}`)}
                        className="hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="px-6 py-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">
                                {message.subject}
                              </span>
                              {(message.replyCount ?? 0) > 0 && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                  </svg>
                                  {message.replyCount}
                                </span>
                              )}
                            </div>
                            {message.event && (
                              <p className="text-sm text-gray-500">
                                Event: {message.event.name}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2.5 py-0.5 text-xs font-medium ${typeConfig.className}`}>
                            {typeConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {message.recipientCount || 0} recipient{(message.recipientCount || 0) !== 1 ? 's' : ''}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(message.sentAt)}
                          </div>
                          <div className="text-sm text-gray-500">
                            by {message.sentBy.firstName} {message.sentBy.lastName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex gap-1">
                            {message.channels.includes('EMAIL') && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                                Email
                              </span>
                            )}
                            {message.channels.includes('SMS') && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                                SMS
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-200">
              {messages.map((message) => {
                const typeConfig = MESSAGE_TYPE_CONFIG[message.messageType];
                return (
                  <div
                    key={message.id}
                    onClick={() => navigate(`/admin/messages/${message.id}`)}
                    className="p-4 cursor-pointer hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {message.subject}
                          </span>
                          {(message.replyCount ?? 0) > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                              </svg>
                              {message.replyCount}
                            </span>
                          )}
                        </div>
                        {message.event && (
                          <p className="text-sm text-gray-500">
                            Event: {message.event.name}
                          </p>
                        )}
                      </div>
                      <span className={`px-2.5 py-0.5 text-xs font-medium ${typeConfig.className}`}>
                        {typeConfig.label}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-sm text-gray-500">
                      <span>{message.recipientCount || 0} recipients</span>
                      <span>{formatDate(message.sentAt)}</span>
                    </div>
                    <div className="mt-2 flex gap-1">
                      {message.channels.includes('EMAIL') && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                          Email
                        </span>
                      )}
                      {message.channels.includes('SMS') && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                          SMS
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} messages
          </p>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Compose Modal */}
      <ComposeMessageModal
        isOpen={showComposeModal}
        onClose={() => setShowComposeModal(false)}
        onSuccess={fetchMessages}
      />
    </div>
  );
}
